import { useState, type ChangeEvent, type ReactNode } from "react";
import { CostSummary } from "./CostSummary";
import { LineItemsField } from "./LineItemsField";
import {
  DEFAULT_TAX_RATE_BPS,
  parseTaxPercent,
  taxPercentToInput,
  taxRatePercentLabel,
} from "./costing";
import type { LineItemDraft, ScopeDraft } from "./types";
import styles from "./ScopeEditor.module.css";

export interface ScopeEditorProps {
  /** The description, priced rows and tax rate being edited. */
  value: ScopeDraft;
  /** Receives the whole scope on every edit; the host owns the state. */
  onChange: (next: ScopeDraft) => void;
  /** Heading over the description block. */
  descriptionTitle?: string;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
  /** Small print under the description field. */
  descriptionNote?: ReactNode;
  /**
   * Control rendered opposite the description label — the job form puts its
   * "Copy from Job Description" button here. Anything host-specific belongs in
   * this slot rather than behind a flag inside the editor.
   */
  descriptionAction?: ReactNode;
  itemsTitle?: string;
  /** What an empty line-item list means for this host. */
  itemsEmptyMessage?: string;
  summaryTitle?: string;
  /** Small print under the GST rate field. */
  taxNote?: ReactNode;
}

/**
 * The priced-work editor: a description, repeatable line items, a GST rate and
 * the derived cost summary.
 *
 * This is the one implementation of that tool. The job form's Scope page and
 * the standalone invoice builder both render it and differ only in the slots
 * they fill — headings, notes, and the action beside the description label.
 * Nothing here knows what a job is.
 *
 * Presentational and stateless apart from the tax-rate input's own text: the
 * host holds the scope and receives a whole replacement on every edit, exactly
 * as {@link LineItemsField} treats its rows.
 */
export function ScopeEditor({
  value,
  onChange,
  descriptionTitle = "Scope of work",
  descriptionLabel = "Description",
  descriptionPlaceholder = "Describe the work being billed",
  descriptionNote,
  descriptionAction,
  itemsTitle = "Line items",
  itemsEmptyMessage,
  summaryTitle = "Cost summary",
  taxNote,
}: ScopeEditorProps) {
  // The typed percentage is local so intermediate states ("1", "12.") are not
  // rounded under the cursor; the canonical basis points go up on every change.
  const [taxPercentText, setTaxPercentText] = useState(() => taxPercentToInput(value.taxRateBps));

  const handleDescriptionChange = (event: ChangeEvent<HTMLTextAreaElement>): void =>
    onChange({ ...value, description: event.target.value });
  const handleItemsChange = (lineItems: LineItemDraft[]): void => onChange({ ...value, lineItems });
  const handleTaxRateChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const text = event.target.value;
    setTaxPercentText(text);
    onChange({ ...value, taxRateBps: parseTaxPercent(text) });
  };

  return (
    <>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{descriptionTitle}</h3>
        <label className={styles.field}>
          <span className={styles.labelRow}>
            <span className={styles.label}>{descriptionLabel}</span>
            {descriptionAction}
          </span>
          <textarea
            className={styles.textarea}
            name="scopeDescription"
            rows={4}
            value={value.description}
            placeholder={descriptionPlaceholder}
            onChange={handleDescriptionChange}
          />
          {descriptionNote && <span className={styles.note}>{descriptionNote}</span>}
        </label>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{itemsTitle}</h3>
        <LineItemsField
          items={value.lineItems}
          onChange={handleItemsChange}
          emptyMessage={itemsEmptyMessage}
        />
      </section>

      <section className={`${styles.section} ${styles.costSection}`}>
        <h3 className={styles.sectionTitle}>{summaryTitle}</h3>
        <div className={styles.summaryRow}>
          <label className={styles.field}>
            <span className={styles.label}>GST rate (%)</span>
            <input
              className={styles.taxInput}
              name="scopeTaxRate"
              type="text"
              inputMode="decimal"
              value={taxPercentText}
              placeholder={taxPercentToInput(value.taxRateBps) || "10"}
              onChange={handleTaxRateChange}
            />
            <span className={styles.note}>
              {taxNote ?? (
                <>
                  Defaults to {taxRatePercentLabel(DEFAULT_TAX_RATE_BPS)}; use 0 for GST-free work.
                </>
              )}
            </span>
          </label>
          <CostSummary items={value.lineItems} taxRateBps={value.taxRateBps} />
        </div>
      </section>
    </>
  );
}
